import { forwardRef, useCallback, useRef } from 'react';
import cn from 'classnames';

import { ChatMessage, isAgentTransferMessage, isExchangeMessage, messageHasWidgetType  } from '@chewy/chat-library-common';
import { FloatingCircles } from './animations/FloatingCircles';

import AiChatMessage from './AiChatMessage';
import styles from './AiConversationPanel.module.scss';
import { useConversation } from '@chewy/chat-library-common';

const curveSvg = (
  <svg
    className={styles.curve}
    viewBox="0 0 400 800"
    preserveAspectRatio="none"
    aria-hidden
  >
    <defs>
      <linearGradient
        id="curveGradient"
        x1="0"
        y1="0"
        x2="0"
        y2="800"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0%" stopColor="var(--gradient-start)" />
        <stop offset="80%" stopColor="var(--gradient-midpoint)" />
        <stop offset="100%" stopColor="var(--gradient-end)" />
      </linearGradient>
      <path
        id="curvePath"
        d="M0 28
           Q120 68 400 20
           V800
           H0
           Z"
      />
    </defs>
    <use href="#curvePath" fill={`url(#curveGradient)`}/>
  </svg>
);

/** An array of ChatMessages */
type Response = ChatMessage[];

/** An array of Responses, or: an array of an array of ChatMessages */
type Exchange = Response[];

/** A stage of the chat -- AI Bot versus Human Agent. Max two per conversation. */
type Stage = Exchange[];

const getConversationStage = (stage: Stage): 'agent' | 'cai' =>
  stage.some(exchange =>
    exchange.some(response =>
      response.some(message => message.responseType === 'agent' || isAgentTransferMessage(message))
    )
  ) ? 'agent' : 'cai';

const ThinkingBubbleMessage = forwardRef<HTMLDivElement, { accessibilityText?: string }>(
  ({ accessibilityText = 'Cai is thinking' }, ref) => (
    <div className={styles.message} ref={ref}>
      {accessibilityText && accessibilityText.length > 0 && (
        <div className={styles.screenReaderOnly}>{accessibilityText}</div>
      )}
      <div className={styles.thinkingBubbles} aria-hidden="true">
        <FloatingCircles playing={true} />
      </div>
    </div>
  )
);

export const AiConversationMessages = ({
  messages,
  shouldScrollToMessage,
  messageRefs,
  typingIndicator
}: {
  messages: ChatMessage[],
  shouldScrollToMessage: (message: ChatMessage) => boolean,
  messageRefs: {
    finalExchangeRef: React.RefObject<HTMLDivElement>,
    curveMessageRef: (element: HTMLDivElement | null) => void,
    transferMessageRef: (element: HTMLDivElement | null) => void,
  },
  typingIndicator?: React.ReactNode
}) => {
  const scrolledMessageIdsRef = useRef<Set<string>>(new Set());
  const scrolledBubbleMessageIdRef = useRef<string | null>();
  const thinkingBubbleRef = useRef<HTMLDivElement>(null);
  const { requestScroll, isBotTurn, phase } = useConversation();

  const { finalExchangeRef, curveMessageRef, transferMessageRef } = messageRefs;

  // Partition messages into:
  // [ stages -> [ exchanges -> [ responses -> [ messages ] ] ] ]
  const messageReducer = (acc: Stage[], msg: ChatMessage): Stage[] => {
    if (acc.length === 0) {
      acc.push([[[msg]]]);
    } else {
      const finalStage = acc[acc.length - 1];

      // If this is a transfer message, we want to start a new stage for it.
      // Otherwise, we want to group messages into exchanges and responses.
      if (isAgentTransferMessage(msg)) {
        acc.push([[[msg]]]);
      } else {
        if (isExchangeMessage(msg)) {

          finalStage.push([[msg]]);
        } else {
          // Non-user message: append to the last response of the last exchange,
          // or create a new response if the responseType differs.
          if (finalStage.length === 0) {
            // No exchange yet (edge case) — create one with this non-user
            // response.
            finalStage.push([[msg]]);
          } else {
            const lastExchange = finalStage[finalStage.length - 1];
            const lastResponse = lastExchange[lastExchange.length - 1];
            if (lastResponse[0].responseType !== msg.responseType) {
              // Start a new response inside the current exchange.
              lastExchange.push([msg]);
            } else {
              // Same responseType as the last response: append to it.
              lastResponse.push(msg);
            }
          }
        }
      }
    }

    return acc;
  };

  const stages = messages.reduce(messageReducer, []);

  /** Whether the final message in the conversation is a user message */
  const isFinalMessageUserMessage = messages.length > 0 && messages[messages.length - 1].responseType === 'user';
  const isFinalMessageTextHeart =
    messages.length > 0 && messageHasWidgetType(messages[messages.length - 1], 'TextHeartAnimation');
  const isFinalMessageWelcomeBack =
    messages.length > 0 && messageHasWidgetType(messages[messages.length - 1], 'WelcomeBack');
  const messageBeforeFinalWelcomeBack = isFinalMessageWelcomeBack
    ? messages[messages.length - 2]
    : undefined;

  const shouldAttachCurveToMessage = ({
    message,
    isFinalExchange,
    exchangeHasTextHeart,
    isTextWithHeart,
    isFirstUserMessage,
  }: {
    message: ChatMessage,
    isFinalExchange: boolean,
    exchangeHasTextHeart: boolean,
    isTextWithHeart: boolean,
    isFirstUserMessage: boolean,
  }) => {
    if (!isFinalExchange) {
      return false;
    }

    if (messageBeforeFinalWelcomeBack) {
      return message === messageBeforeFinalWelcomeBack;
    }

    if (exchangeHasTextHeart) {
      return isTextWithHeart;
    }

    return isFirstUserMessage;
  };

  const scrollToBubble = useCallback(() => {
    thinkingBubbleRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }, []);

  return (
    <>
      {stages.map((exchanges, stageIndex) => (
        <div
          className={cn(styles.stage, styles[`stage_${getConversationStage(exchanges)}`])}
          key={stageIndex}
          data-conversation-stage={getConversationStage(exchanges)}
          data-stage-index={stageIndex}
        >
          {exchanges.map((exchange, exchangeIndex) => {
            /** Whether this is the last exchange in the final stage */
            const isFinalStage = stageIndex === stages.length - 1;
            const isFinalExchange = isFinalStage && exchangeIndex === exchanges.length - 1;

            /** Whether this Exchange has any message with a text heart in it */
            const exchangeHasTextHeart = exchange.some(response =>
              response.some(message => messageHasWidgetType(message, 'TextHeartAnimation'))
            );

            /** Whether this Exchange has any message with a chat wrapup card in it */
            const exchangeHasChatWrapupCard = exchange.some(response =>
              response.some(message => messageHasWidgetType(message, 'ChatWrapupCard'))
            );

            return (
              <div
                // We are using indices until we can get a stable ID on these
                // messages. We used to use the message ID with an index backup,
                // but because the message ID was undefined first, and defined
                // later, the key would change, and the animation would restart.
                key={exchangeIndex}
                className={cn(styles.exchange, exchangeHasChatWrapupCard && styles.exchange_wrapupCard)}
                {...(isFinalExchange && {
                  ref: finalExchangeRef
                })}
              >
                {isFinalExchange && (
                  <div className={cn(
                    styles.curveContainer,
                    isFinalMessageWelcomeBack && styles.curveContainer_welcomeBack
                  )}>
                    {curveSvg}
                    <div className={styles.curveBottom} />
                  </div>
                )}
                {exchange.map((response, responseIndex) => {
                  /** Whether this Response has any message with a text heart in it */
                  const responseHasTextWithHeart = response.some(message => messageHasWidgetType(message, 'TextHeartAnimation'));

                  const responseHasWelcomeBack = response.some(message => messageHasWidgetType(message, 'WelcomeBack'));
                  // Only Transferring/Transferred — not AgentJoined. isAgentTransferMessage
                  // includes AgentJoined (stage boundary), which would keep the wait bubble
                  // visible after the agent connects and after chat ends.
                  const responseIsAwaitingAgent = response.some(message =>
                    messageHasWidgetType(message, ['Transferring', 'Transferred'])
                  ) && phase === 'transferred';

                  // If this is the last response in the first stage (and there
                  // are ate least two stages) -- i.e.,
                  // the "Let's get you connected to an agent" message.
                  const isTransferMessage =
                    responseIndex === exchange.length - 1 && stageIndex === 0 && stages.length > 1;

                  const isBotResponse = response[0].responseType === 'aiBot';

                  return (
                    <div
                      // We are using indices until we can get a stable ID on these
                      // messages. We used to use the message ID with an index backup,
                      // but because the message ID was undefined first, and defined
                      // later, the key would change, and the animation would restart.
                      key={responseIndex}
                      {...(isTransferMessage && {
                        ref: transferMessageRef,
                        'data-transfer-message': true
                      })}
                      className={cn(
                        styles.response,
                        styles[`response_${response[0].responseType}`],
                        {
                          [styles.response_textHeartAnimation]: responseHasTextWithHeart,
                          [styles.response_welcomeBack]: responseHasWelcomeBack,
                        }
                      )}
                    >
                      {response.map((message, messageIndex) => {
                        /** Whether this message has a text with heart */
                        const isTextWithHeart = messageHasWidgetType(message, 'TextHeartAnimation');
                        const isChatWrapupCard = messageHasWidgetType(message, 'ChatWrapupCard');
                        const isOrderCardsPackages = messageHasWidgetType(message, 'OrderCardsPackages');
                        const isAgentJoined = messageHasWidgetType(message, 'AgentJoined');
                        const isWelcomeBack = messageHasWidgetType(message, 'WelcomeBack');
                        /** Whether this is the user message in the last exchange */
                        const isUserMessage = message.responseType === 'user';

                        /** Whether this is the first user message in final exchange */
                        const isFirstUserMessage = isUserMessage && responseIndex === 0 && messageIndex === 0;

                        const isFinalMessage =
                          isFinalStage &&
                          isFinalExchange &&
                          responseIndex === exchange.length - 1 &&
                          messageIndex === response.length - 1;

                        const isCurveMessage = shouldAttachCurveToMessage({
                          message,
                          isFinalExchange,
                          exchangeHasTextHeart,
                          isTextWithHeart,
                          isFirstUserMessage,
                        });

                        const handleScroll = (element: HTMLDivElement) => {
                          // If we've already scrolled to this element, or it
                          // shouldn't be scrolled to, don't scroll
                          if (
                            !requestScroll ||
                            !shouldScrollToMessage(message) ||
                            scrolledMessageIdsRef.current.has(message.id)
                          ) {
                            /**
                             * If this is the final message, scroll to Thinking
                             * Bubble, unless it's the TextHeartAnimation, or
                             * we've already scrolled to the bubble in response
                             * to this message
                             */
                            if (
                              isFinalMessage &&
                              !isTextWithHeart &&
                              scrolledBubbleMessageIdRef.current !== message.id
                            ) {
                              scrolledBubbleMessageIdRef.current = message.id;
                              scrollToBubble();
                            }
                            return;
                          }

                          scrolledMessageIdsRef.current.add(message.id);

                          requestScroll({
                            element,
                            timestamp: message.responseTimestamp,
                            onScrollAllComplete: scrollToBubble
                          });
                        };

                        return (
                          <div
                            // We are using indices until we can get a stable ID on these
                            // messages. We used to use the message ID with an index backup,
                            // but because the message ID was undefined first, and defined
                            // later, the key would change, and the animation would restart.
                            key={messageIndex}
                            {...(isCurveMessage && {
                              ref: curveMessageRef,
                              'data-curve-message': true
                            })}
                            className={cn(
                              styles.message,
                              styles[`message_${message.responseType}`],
                              {
                                [styles.message_final]: isFinalMessage,
                                [styles.message_textWithHeart]: isTextWithHeart,
                                [styles.message_chatWrapupCard]: isChatWrapupCard,
                                [styles.message_orderCardsPackages]: isOrderCardsPackages,
                                [styles.message_agentJoined]: isAgentJoined,
                                [styles.message_welcomeBack]: isWelcomeBack,
                              }
                            )}
                            data-response-type={message.responseType}
                            data-message-type={message.type}
                            data-message-source={message.source}
                          >
                            {/**
                             * Show the thinking bubble inside this response if:
                             * - (bot's turn OR awaiting agent transfer) AND
                             * - this is the final exchange, AND
                             * - this is a bot response, AND
                             * - this is not the TextHeartAnimation (that widget is
                             *   the waiting state for the first bot reply)
                             *
                             * Covers multi-message bot replies and the wait after
                             * Transferring/Transferred while phase is transferred.
                             */}
                            {(isBotTurn || responseIsAwaitingAgent) &&
                            isFinalExchange &&
                            isBotResponse &&
                            !isTextWithHeart ? (
                              <ThinkingBubbleMessage
                                ref={thinkingBubbleRef}
                                accessibilityText={responseIsAwaitingAgent ? '' : undefined}
                              />
                            ) : message.isLoading ? (
                              <div>Loading...</div>
                            ) : (
                              <AiChatMessage message={message} handleScroll={handleScroll} />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}

                {/**
                  * Show the thinking bubble after the last visible message if:
                  * - it is the bot's turn, AND
                  * - this is the final exchange, AND
                  * - the last message is a user message OR the TextHeartAnimation
                  *   (heart is an aiBot wait state, so it would otherwise suppress
                  *   this trailing bubble)
                  */}
                {isBotTurn && isFinalExchange && (isFinalMessageUserMessage || isFinalMessageTextHeart) && (
                  <div className={cn(styles.response, styles.response_aiBot)}>
                    <ThinkingBubbleMessage ref={thinkingBubbleRef} />
                  </div>
                )}
                {isFinalExchange && typingIndicator && (
                  <div className={styles.response}>{typingIndicator}</div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </>
  );
};
